//#region ../src/utils/qr-payload/bit-alphabet.ts
/**
* Build a codec for `alphabet` where each character carries `bitsPerChar`
* bits (alphabet length must be exactly `2^bitsPerChar`; validated once at
* construction because a mismatch is a programming error, not bad data).
*/
function createBitAlphabetCodec(alphabet, bitsPerChar) {
	if (alphabet.length !== 1 << bitsPerChar) throw new Error(`alphabet length ${alphabet.length} does not match ${bitsPerChar} bits/char`);
	const reverse = /* @__PURE__ */ new Map();
	for (let i = 0; i < alphabet.length; i++) reverse.set(alphabet.charAt(i), i);
	const mask = (1 << bitsPerChar) - 1;
	function encode(bytes) {
		let out = "";
		let buffer = 0;
		let bits = 0;
		for (const byte of bytes) {
			buffer = buffer << 8 | byte;
			bits += 8;
			while (bits >= bitsPerChar) {
				bits -= bitsPerChar;
				out += alphabet.charAt(buffer >> bits & mask);
			}
			buffer &= (1 << bits) - 1;
		}
		if (bits > 0) out += alphabet.charAt(buffer << bitsPerChar - bits & mask);
		return out;
	}
	function decode(text) {
		if (typeof text !== "string") return null;
		let buffer = 0;
		let bits = 0;
		const out = [];
		for (const char of text) {
			const value = reverse.get(char);
			if (value === void 0) return null;
			buffer = buffer << bitsPerChar | value;
			bits += bitsPerChar;
			if (bits >= 8) {
				bits -= 8;
				out.push(buffer >> bits & 255);
				buffer &= (1 << bits) - 1;
			}
		}
		if (bits >= bitsPerChar || buffer !== 0) return null;
		return Uint8Array.from(out);
	}
	return {
		encode,
		decode
	};
}
//#endregion
//#region ../src/utils/qr-payload/base64url.ts
/**
* Unpadded base64url (RFC 4648 §5) over the shared bit-alphabet codec —
* the default URL transport for binary codec output in the QR
* payload-compression benchmark (plan §6 P2). The URL-safe alphabet
* survives `encodeURIComponent` unchanged, so it adds zero percent-escaping
* overhead inside a query parameter; note that '_' and lowercase letters
* force QR byte mode (8 bits/char) — see `qr-size-estimator.ts`.
*/
const CODEC = createBitAlphabetCodec("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_", 6);
/** Encode bytes as unpadded base64url. */
function encodeBase64Url(bytes) {
	return CODEC.encode(bytes);
}
/**
* Decode unpadded base64url. Total: `null` for padding, foreign characters,
* impossible lengths or non-canonical trailing bits — never throws.
*/
function decodeBase64Url(text) {
	return CODEC.decode(text);
}
//#endregion
export { encodeBase64Url as n, createBitAlphabetCodec as r, decodeBase64Url as t };
